import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import { HOCKEY_AGE_GROUPS, getAvailableTiers } from '@/lib/hockeyData'
import { toast } from 'sonner'

export default function ScrimmageManagerSettings({ teamId, team }) {
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [scrimmageManager, setScrimmageManager] = useState(null)
  const [loading, setLoading] = useState(true)

  // Team location & level
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [sportType, setSportType] = useState('hockey')
  const [ageGroup, setAgeGroup] = useState('')
  const [teamTier, setTeamTier] = useState('')

  // Scrimmage manager info
  const [managerName, setManagerName] = useState('')
  const [managerEmail, setManagerEmail] = useState('')
  const [managerPhone, setManagerPhone] = useState('')
  const [displayEmail, setDisplayEmail] = useState(true)
  const [displayPhone, setDisplayPhone] = useState(false)

  useEffect(() => {
    if (teamId && team) {
      // Load team data
      setCity(team.city || '')
      setState(team.state || '')
      setZipCode(team.zip_code || '')
      setSportType(team.sport_type || 'hockey')
      setAgeGroup(team.team_level || '')
      setTeamTier(team.level_detail || '')

      // Load scrimmage manager
      fetchScrimmageManager()
    }
  }, [teamId, team])

  const fetchScrimmageManager = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('scrimmage_managers')
        .select('*')
        .eq('team_id', teamId)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') throw error

      if (data) {
        setScrimmageManager(data)
        setManagerName(data.name || '')
        setManagerEmail(data.email || '')
        setManagerPhone(data.phone || '')
        setDisplayEmail(data.display_email ?? true)
        setDisplayPhone(data.display_phone ?? false)
      } else {
        // Default to team manager's info
        setManagerEmail(team.manager_email || '')
        setManagerName(team.manager_name || '')
      }
    } catch (err) {
      console.error('Error fetching scrimmage manager:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTeamInfo = async () => {
    // Validate required fields (only for user-managed teams)
    if (team?.data_source !== 'seed_auto' && (!sportType || !ageGroup)) {
      toast.error('Please set your sport type and age group to use scrimmage features.')
      return
    }

    setSaving(true)
    setSuccess(false)
    try {
      // Build update object based on team type
      const teamUpdateData = {
        city,
        state,
        zip_code: zipCode,
        updated_at: new Date().toISOString()
      }
      
      // Only allow level changes for user-managed teams
      if (team?.data_source !== 'seed_auto') {
        teamUpdateData.sport_type = sportType
        teamUpdateData.team_level = ageGroup
        teamUpdateData.level_detail = teamTier || null
      }

      // Update team location (and level for user-managed teams)
      const { error: teamError } = await supabase
        .from('teams')
        .update(teamUpdateData)
        .eq('id', teamId)

      if (teamError) throw teamError

      // Upsert scrimmage manager
      if (managerName && managerEmail) {
        const managerData = {
          team_id: teamId,
          name: managerName,
          email: managerEmail,
          phone: managerPhone || null,
          display_email: displayEmail,
          display_phone: displayPhone,
          updated_at: new Date().toISOString()
        }

        if (scrimmageManager) {
          const { error: updateError } = await supabase
            .from('scrimmage_managers')
            .update(managerData)
            .eq('id', scrimmageManager.id)
          
          if (updateError) throw updateError
        } else {
          const { error: insertError } = await supabase
            .from('scrimmage_managers')
            .insert(managerData)
          
          if (insertError) throw insertError
        }
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      
      // Refresh data
      await fetchScrimmageManager()
    } catch (err) {
      console.error('Error saving scrimmage settings:', err)
      toast.error('Failed to save scrimmage settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8 text-brand-slate">Loading...</div>
      </Card>
    )
  }

  const availableTiers = getAvailableTiers(ageGroup)

  return (
    <Card>
      <h2 className="font-heading text-xl font-bold mb-4">
        Scrimmage Manager Setup
      </h2>
      <p className="text-sm text-brand-slate mb-6">
        Configure your team's location and scrimmage coordinator information. This data will be displayed on the public scrimmage bulletin board.
      </p>

      <div className="space-y-6">
        {/* Team Location */}
        <div>
          <h3 className="font-medium text-brand-charcoal mb-3">Team Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Victoria"
              required
            />
            <Input
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="e.g., MN"
              maxLength={2}
              required
            />
            <Input
              label="Zip Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="e.g., 55386"
              maxLength={10}
            />
          </div>
          <p className="mt-2 text-xs text-brand-slate">
            This location is used for radius-based scrimmage searches
          </p>
        </div>

        {/* Sport Type */}
        <div>
          <Select
            label="Sport Type"
            value={sportType}
            onChange={(e) => setSportType(e.target.value)}
            options={[{ value: 'hockey', label: 'Hockey' }]}
            required
          />
        </div>

        {/* Team Level */}
        <div>
          <h3 className="font-medium text-brand-charcoal mb-3">Team Information</h3>
          {team?.data_source === 'seed_auto' ? (
            // Read-only display for seed teams
            <div className="space-y-3">
              <div>
                <label className="label">Team Name</label>
                <div className="mt-1 p-3 bg-gray-50 border border-border rounded text-brand-charcoal">
                  {team.name}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Age Group</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-border rounded text-brand-charcoal">
                    {HOCKEY_AGE_GROUPS.find(ag => ag.value === ageGroup)?.label || ageGroup || 'Not set'}
                  </div>
                </div>
                <div>
                  <label className="label">Team Tier</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-border rounded text-brand-charcoal">
                    {teamTier || 'None'}
                  </div>
                </div>
              </div>
              <p className="text-xs text-brand-slate">
                Team level information is managed by the association database. Contact support if this needs to be corrected.
              </p>
            </div>
          ) : (
            // Editable for user-managed teams
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Age Group"
                value={ageGroup}
                onChange={(e) => {
                  setAgeGroup(e.target.value)
                  setTeamTier('') // Reset tier when age group changes
                }}
                options={[
                  { value: '', label: 'Select age group' },
                  ...HOCKEY_AGE_GROUPS
                ]}
                required
              />
              
              {availableTiers.length > 0 && (
                <Select
                  label="Team Tier (Optional)"
                  value={teamTier}
                  onChange={(e) => setTeamTier(e.target.value)}
                  options={[
                    { value: '', label: 'No tier' },
                    ...availableTiers
                  ]}
                />
              )}
            </div>
          )}
        </div>

        {/* Scrimmage Manager Contact */}
        <div>
          <h3 className="font-medium text-brand-charcoal mb-3">Scrimmage Coordinator</h3>
          <div className="space-y-4">
            <Input
              label="Name"
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              placeholder="e.g., John Smith"
              required
            />
            
            <Input
              label="Email"
              type="email"
              value={managerEmail}
              onChange={(e) => setManagerEmail(e.target.value)}
              placeholder="e.g., coordinator@example.com"
              required
            />
            
            <Input
              label="Phone (Optional)"
              type="tel"
              value={managerPhone}
              onChange={(e) => setManagerPhone(e.target.value)}
              placeholder="e.g., (612) 555-1234"
            />

            {/* Contact Display Preferences */}
            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium text-brand-charcoal mb-3">
                Display on Public Bulletin Board:
              </p>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayEmail}
                    onChange={(e) => setDisplayEmail(e.target.checked)}
                    className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <span className="text-sm text-brand-charcoal">Show email address</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={displayPhone}
                    onChange={(e) => setDisplayPhone(e.target.checked)}
                    className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <span className="text-sm text-brand-charcoal">Show phone number</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-brand-slate">
                Choose which contact methods to display when teams view your scrimmage postings
              </p>
            </div>
          </div>
        </div>

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            ✓ Scrimmage settings saved successfully!
          </div>
        )}

        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={handleSaveTeamInfo}
            disabled={saving || !city || !state || !sportType || !ageGroup || !managerName || !managerEmail}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

